import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Paper, Alert, Chip,
  TextField, MenuItem, Grid, Slider, Dialog, DialogTitle, DialogContent, DialogActions,
  LinearProgress, Tooltip, CircularProgress,
} from '@mui/material';
import {
  ArrowBack, Stop, Save, PlayArrow, Explore, ArrowUpward, ArrowDownward,
  ArrowBack as ArrowLeft, ArrowForward as ArrowRight, Speed, MyLocation,
  ZoomIn, ZoomOut, Refresh, Warning, Tune, FitScreen,
} from '@mui/icons-material';
import { Stage, Layer, Circle, Line, Rect, Image as KonvaImage } from 'react-konva';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppHooks';
import { fetchMyRobots } from '../../features/robots/robotsSlice';
import { startSlam, stopSlam, saveSlamMap } from '../../features/maps/mapsSlice';
import { useWebSocket } from '../../hooks/useWebSocket';
import { useTeleopController } from '../../hooks/useTeleopController';
import { addEventListener, removeEventListener, slamViewStart, slamViewStop } from '../../utils/socketClient';
import { SlamMapData } from '../../types';

const SlamViewer: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { robots } = useAppSelector((state) => state.robots);
  const { isSlamActive } = useAppSelector((state) => state.maps);
  const { robotsOnline, subscribeToRobot, getRobotTelemetry, sendTeleop, isConnected } = useWebSocket();

  const [selectedRobotId, setSelectedRobotId] = useState<string>(searchParams.get('robot') || '');
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; message: string } | null>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [mapName, setMapName] = useState('');
  const [zoom, setZoom] = useState(1);
  const [maxSpeed, setMaxSpeed] = useState(0.3);
  const [commandRateHz, setCommandRateHz] = useState(10);
  const [slamMapData, setSlamMapData] = useState<SlamMapData | null>(null);
  const [mapImage, setMapImage] = useState<HTMLImageElement | null>(null);
  const [slamStarting, setSlamStarting] = useState(false);
  const [slamStopping, setSlamStopping] = useState(false);
  const [localSlamActive, setLocalSlamActive] = useState(false);
  
  // SLAM auto-abort on disconnect
  const disconnectCountRef = useRef(0);
  const lastOnlineRef = useRef(true);

  const canvasWidth = 700;
  const canvasHeight = 500;

  useEffect(() => { dispatch(fetchMyRobots()); }, [dispatch]);

  const selectedRobot = robots.find((r) => r.id === selectedRobotId);

  useEffect(() => {
    if (selectedRobot) {
      subscribeToRobot(selectedRobot.serial_number);
    }
  }, [selectedRobot, subscribeToRobot]);

  const telemetry = selectedRobot ? getRobotTelemetry(selectedRobot.serial_number) : null;
  const isOnlineFromList = selectedRobot ? robotsOnline.includes(selectedRobot.serial_number) : false;
  const hasRecentTelemetry = telemetry && (Date.now() - telemetry.timestamp < 10000);
  const isRobotOnline = isOnlineFromList || !!hasRecentTelemetry;

  // Use teleop controller hook with new interface
  const { activeKeys, handleKeyDown, handleKeyUp, emergencyStop, lastCommand } = useTeleopController({
    robotSerial: selectedRobot?.serial_number,
    isOnline: isRobotOnline,
    sendTeleop,
    commandRateHz,
    maxSpeed,
    enabled: localSlamActive,
  });

  // SLAM view tracking - notify backend when viewing/leaving SLAM page
  useEffect(() => {
    if (selectedRobot?.serial_number && isConnected) {
      // Notify backend that user started viewing SLAM
      slamViewStart(selectedRobot.serial_number);
      console.log('SLAM view started for:', selectedRobot.serial_number);
      
      // Cleanup: notify when leaving
      return () => {
        slamViewStop(selectedRobot.serial_number);
        console.log('SLAM view stopped for:', selectedRobot.serial_number);
      };
    }
  }, [selectedRobot?.serial_number, isConnected]);

  // Reset SLAM state when WebSocket disconnects (e.g., logout)
  useEffect(() => {
    if (!isConnected) {
      console.log('WebSocket disconnected - resetting SLAM state');
      setLocalSlamActive(false);
      setSlamStarting(false);
      setSlamStopping(false);
      setSlamMapData(null);
      setMapImage(null);
    }
  }, [isConnected]);

  // Auto-abort SLAM on robot disconnect
  useEffect(() => {
    if (localSlamActive) {
      if (!isRobotOnline && lastOnlineRef.current) {
        disconnectCountRef.current++;
        console.log(`SLAM active, robot disconnected (${disconnectCountRef.current}/3)`);
        
        if (disconnectCountRef.current >= 3) {
          setAlertMessage({ type: 'warning', message: 'SLAM aborted due to robot disconnection' });
          setLocalSlamActive(false);
          disconnectCountRef.current = 0;
        }
      } else if (isRobotOnline) {
        disconnectCountRef.current = 0;
      }
      lastOnlineRef.current = isRobotOnline;
    }
  }, [isRobotOnline, localSlamActive]);

  // Listen for SLAM map updates
  useEffect(() => {
    const handleSlamMapUpdate = async (data: any) => {
      console.log('SLAM map update received:', {
        robot_serial: data.robot_serial,
        width: data.width || data.map?.width,
        height: data.height || data.map?.height,
        compressed: data.compressed
      });
      
      if (selectedRobot && data.robot_serial === selectedRobot.serial_number) {
        // Handle both top-level and nested map data
        let mapData = data.data || data.map?.data;
        const mapWidth = data.width || data.map?.width;
        const mapHeight = data.height || data.map?.height;
        const mapResolution = data.resolution || data.map?.resolution;
        const mapOrigin = data.origin || data.map?.origin;
        
        // Decompress if needed
        if (data.compressed && typeof mapData === 'string') {
          try {
            // Decode base64 and decompress using pako
            const binaryString = atob(mapData);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            
            // Use pako to decompress (need to import dynamically or use built-in)
            const pako = await import('pako');
            const decompressed = pako.inflate(bytes);
            
            // Convert back to occupancy values (undo the +1 offset)
            mapData = Array.from(decompressed).map(v => (v === 0 ? -1 : v - 1));
            console.log(`Decompressed map: ${mapData.length} cells`);
          } catch (e) {
            console.error('Failed to decompress map:', e);
            return;
          }
        }
        
        // Store for robot position calculation
        setSlamMapData({
          ...data,
          width: mapWidth,
          height: mapHeight,
          resolution: mapResolution,
          origin: mapOrigin,
          data: mapData
        });
        
        // Convert occupancy grid to image with vertical flip
        // ROS occupancy grid: row 0 is at origin.y (bottom of map)
        // Canvas: row 0 is at top
        // So we need to flip vertically
        if (mapData && mapWidth && mapHeight) {
          console.log(`Converting map: ${mapWidth}x${mapHeight}`);
          
          const canvas = document.createElement('canvas');
          canvas.width = mapWidth;
          canvas.height = mapHeight;
          const ctx = canvas.getContext('2d');
          
          if (ctx) {
            const imageData = ctx.createImageData(mapWidth, mapHeight);
            
            for (let row = 0; row < mapHeight; row++) {
              for (let col = 0; col < mapWidth; col++) {
                // Flip Y: read from bottom row first
                const srcRow = mapHeight - 1 - row;
                const srcIdx = srcRow * mapWidth + col;
                const value = mapData[srcIdx];
                
                let color: number;
                if (value === -1) {
                  color = 128; // Unknown - gray
                } else if (value === 0) {
                  color = 255; // Free - white
                } else {
                  color = Math.max(0, 255 - (value * 2.55)); // Occupied - dark
                }
                
                const dstIdx = (row * mapWidth + col) * 4;
                imageData.data[dstIdx] = color;     // R
                imageData.data[dstIdx + 1] = color; // G
                imageData.data[dstIdx + 2] = color; // B
                imageData.data[dstIdx + 3] = 255;   // A
              }
            }
            
            ctx.putImageData(imageData, 0, 0);
            
            const img = new Image();
            img.onload = () => {
              console.log('Map image created');
              setMapImage(img);
            };
            img.src = canvas.toDataURL();
          }
        }
      }
    };

    const handleSlamStatus = (data: any) => {
      if (selectedRobot && data.robot_serial === selectedRobot.serial_number) {
        console.log('SLAM status:', data);
        const statusMessage = data.message || '';
        
        if (data.status === 'starting') {
          setSlamStarting(true);
          setAlertMessage({ type: 'info', message: statusMessage || 'Starting SLAM... Please wait.' });
        } else if (data.status === 'started') {
          setLocalSlamActive(true);
          setSlamStarting(false);
          setAlertMessage({ type: 'success', message: statusMessage || 'SLAM started! Use WASD keys to drive the robot and build the map.' });
        } else if (data.status === 'stopping') {
          setSlamStopping(true);
          setAlertMessage({ type: 'info', message: statusMessage || 'Stopping SLAM... Please wait.' });
        } else if (data.status === 'stopped') {
          setLocalSlamActive(false);
          setSlamStopping(false);
          // Don't show save dialog - user should save BEFORE stopping SLAM
          setAlertMessage({ type: 'success', message: statusMessage || 'SLAM stopped. Normal navigation mode resumed.' });
        } else if (data.status === 'aborted') {
          setLocalSlamActive(false);
          setSlamStarting(false);
          setSlamStopping(false);
          setAlertMessage({ type: 'warning', message: statusMessage || 'SLAM was aborted.' });
        } else if (data.status === 'saving') {
          setAlertMessage({ type: 'info', message: statusMessage || 'Saving map...' });
        } else if (data.status === 'uploading') {
          setAlertMessage({ type: 'info', message: statusMessage || 'Uploading map to cloud...' });
        } else if (data.status === 'saved') {
          setSaveDialogOpen(false);
          setMapName('');
          setAlertMessage({ type: 'success', message: statusMessage || 'Map saved successfully!' });
        } else if (data.status === 'error') {
          setSlamStarting(false);
          setSlamStopping(false);
          setAlertMessage({ type: 'error', message: statusMessage || 'SLAM error occurred. Check robot logs.' });
        }
      }
    };

    // Handle map upload result from cloud
    const handleMapUploadResult = (data: any) => {
      if (selectedRobot && data.robot_serial === selectedRobot.serial_number) {
        console.log('Map upload result:', data);
        if (data.success) {
          setSaveDialogOpen(false);
          setMapName('');
          setAlertMessage({ 
            type: 'success', 
            message: `Map "${data.map_name}" uploaded successfully! (ID: ${data.map_id?.substring(0, 8)}...)` 
          });
        } else {
          setAlertMessage({ 
            type: 'error', 
            message: `Map upload failed: ${data.error || 'Unknown error'}` 
          });
        }
      }
    };

    addEventListener('slam_map_update', handleSlamMapUpdate);
    addEventListener('slam_status', handleSlamStatus);
    addEventListener('map_upload_result', handleMapUploadResult);

    return () => {
      removeEventListener('slam_map_update', handleSlamMapUpdate);
      removeEventListener('slam_status', handleSlamStatus);
      removeEventListener('map_upload_result', handleMapUploadResult);
    };
  }, [selectedRobot]);

  // Keyboard event handlers
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isRobotOnline || !localSlamActive) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      
      const key = e.key.toLowerCase();
      if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright', ' '].includes(key)) {
        e.preventDefault();
        handleKeyDown(key);
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright', ' '].includes(key)) {
        handleKeyUp(key);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [isRobotOnline, localSlamActive, handleKeyDown, handleKeyUp]);

  const handleStartSlam = async () => {
    if (!selectedRobotId || !isRobotOnline) return;
    setSlamStarting(true);
    setAlertMessage({ type: 'info', message: 'Starting SLAM... Please wait for launch file to initialize.' });
    
    try {
      await dispatch(startSlam(selectedRobotId)).unwrap();
      // Status will be updated via WebSocket slam_status event
    } catch (e: any) {
      setSlamStarting(false);
      setAlertMessage({ type: 'error', message: e || 'Failed to start SLAM' });
    }
  };
  
  // Confirmation dialog for starting SLAM
  const [slamConfirmOpen, setSlamConfirmOpen] = useState(false);
  
  const handleStartSlamClick = () => {
    setSlamConfirmOpen(true);
  };
  
  const handleConfirmStartSlam = () => {
    setSlamConfirmOpen(false);
    handleStartSlam();
  };

  const handleStopSlam = async () => {
    if (!selectedRobotId) return;
    setSlamStopping(true);
    
    try {
      await dispatch(stopSlam(selectedRobotId)).unwrap();
      // Status will be updated via WebSocket slam_status event
    } catch (e: any) {
      setSlamStopping(false);
      setAlertMessage({ type: 'error', message: e || 'Failed to stop SLAM' });
    }
  };

  const handleSaveMap = async () => {
    if (!selectedRobotId || !mapName.trim()) return;
    try {
      await dispatch(saveSlamMap({ robotId: selectedRobotId, name: mapName })).unwrap();
      // Don't close dialog or show success yet - wait for slam_status 'saved' event
      setAlertMessage({ type: 'info', message: 'Saving map... Please wait.' });
    } catch (e: any) {
      setAlertMessage({ type: 'error', message: e || 'Failed to send save command' });
    }
  };

  const handleEmergencyStop = () => {
    emergencyStop();
  };

  // Auto-fit zoom based on map size
  useEffect(() => {
    if (slamMapData && slamMapData.width && slamMapData.height) {
      const fitZoomX = (canvasWidth - 40) / slamMapData.width;
      const fitZoomY = (canvasHeight - 40) / slamMapData.height;
      const fitZoom = Math.min(fitZoomX, fitZoomY, 2);
      if (fitZoom > 0 && fitZoom < 10) {
        setZoom(fitZoom);
      }
    }
  }, [slamMapData?.width, slamMapData?.height]);

  // Calculate robot position on canvas from telemetry pose
  const robotPose = telemetry?.pose || { x: 0, y: 0, theta: 0 };
  
  // Map position calculation
  let robotCanvasX = canvasWidth / 2;
  let robotCanvasY = canvasHeight / 2;
  
  if (slamMapData && mapImage) {
    const mapOriginX = slamMapData.origin?.x || 0;
    const mapOriginY = slamMapData.origin?.y || 0;
    const resolution = slamMapData.resolution || 0.05;
    const mapWidth = slamMapData.width;
    const mapHeight = slamMapData.height;
    
    // Robot position in map pixel coordinates
    const robotMapPixelX = (robotPose.x - mapOriginX) / resolution;
    const robotMapPixelY = (robotPose.y - mapOriginY) / resolution;
    
    // Canvas offset
    const mapCanvasOffsetX = (canvasWidth - mapWidth * zoom) / 2;
    const mapCanvasOffsetY = (canvasHeight - mapHeight * zoom) / 2;
    
    // Robot canvas position (Y flipped because image was flipped)
    robotCanvasX = mapCanvasOffsetX + robotMapPixelX * zoom;
    robotCanvasY = mapCanvasOffsetY + (mapHeight - robotMapPixelY) * zoom;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <IconButton onClick={() => navigate('/maps')}><ArrowBack /></IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>SLAM Mapping</Typography>
          <Typography variant="body2" color="text.secondary">
            Create a map by driving the robot around
          </Typography>
        </Box>
        {selectedRobotId && isRobotOnline && (
          localSlamActive || isSlamActive ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="outlined" 
                color="warning" 
                startIcon={slamStopping ? <CircularProgress size={16} /> : <Stop />} 
                onClick={handleStopSlam}
                disabled={slamStopping}
              >
                {slamStopping ? 'Stopping...' : 'Stop SLAM'}
              </Button>
              <Button variant="contained" startIcon={<Save />} onClick={() => setSaveDialogOpen(true)}>
                Save Map
              </Button>
            </Box>
          ) : (
            <Button 
              variant="contained" 
              startIcon={slamStarting ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />} 
              onClick={handleStartSlamClick}
              disabled={slamStarting || !isRobotOnline}
            >
              {slamStarting ? 'Starting...' : 'Start SLAM'}
            </Button>
          )
        )}
      </Box>

      {/* SLAM Start Confirmation Dialog */}
      <Dialog open={slamConfirmOpen} onClose={() => setSlamConfirmOpen(false)}>
        <DialogTitle>Start SLAM Mapping?</DialogTitle>
        <DialogContent>
          <Typography>
            This will restart the robot's navigation system in SLAM mode. 
            The current navigation will be interrupted.
          </Typography>
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Make sure the robot is in a safe position before proceeding.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSlamConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmStartSlam}>
            Start SLAM
          </Button>
        </DialogActions>
      </Dialog>

      {alertMessage && (
        <Alert severity={alertMessage.type} onClose={() => setAlertMessage(null)} sx={{ mb: 2 }}>
          {alertMessage.message}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Left side - Robot selection & controls */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>Select Robot</Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={selectedRobotId}
                onChange={(e) => {
                  setSelectedRobotId(e.target.value);
                  setSlamMapData(null);
                  setMapImage(null);
                  setLocalSlamActive(false);
                }}
              >
                <MenuItem value="">-- Select Robot --</MenuItem>
                {robots.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {/* v6.3.36: serial first — friendly names can duplicate */}
                    {r.serial_number}{r.friendly_name ? ` (${r.friendly_name})` : ''}
                    {robotsOnline.includes(r.serial_number) ? ' 🟢' : ' 🔴'}
                  </MenuItem>
                ))}
              </TextField>

              {selectedRobot && (
                <Box sx={{ mt: 2 }}>
                  <Chip 
                    size="small" 
                    label={isRobotOnline ? 'Online' : 'Offline'} 
                    color={isRobotOnline ? 'success' : 'error'} 
                    sx={{ mr: 1 }}
                  />
                  <Chip 
                    size="small" 
                    label={localSlamActive ? 'SLAM Active' : 'SLAM Inactive'} 
                    color={localSlamActive ? 'warning' : 'default'}
                  />
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Teleop Controls */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Explore /> Teleop Controls
                </Box>
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {localSlamActive ? 'Use WASD or arrow keys to drive the robot' : 'Start SLAM to enable controls'}
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  {/* Forward Button */}
                  <IconButton 
                    size="large" 
                    disabled={!localSlamActive || !isRobotOnline}
                    onMouseDown={() => handleKeyDown('w')}
                    onMouseUp={() => handleKeyUp('w')}
                    onMouseLeave={() => handleKeyUp('w')}
                    onTouchStart={(e) => { e.preventDefault(); handleKeyDown('w'); }}
                    onTouchEnd={(e) => { e.preventDefault(); handleKeyUp('w'); }}
                    sx={{ 
                      width: 64, 
                      height: 64, 
                      bgcolor: activeKeys.has('w') ? 'primary.main' : 'rgba(255,255,255,0.1)',
                      '&:hover': { bgcolor: 'primary.dark' },
                      '&:disabled': { bgcolor: 'rgba(255,255,255,0.05)' },
                      transition: 'all 0.1s'
                    }}
                  >
                    <ArrowUpward sx={{ fontSize: 32 }} />
                  </IconButton>
                  
                  {/* Left, Stop, Right row */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton 
                      size="large" 
                      disabled={!localSlamActive || !isRobotOnline}
                      onMouseDown={() => handleKeyDown('a')}
                      onMouseUp={() => handleKeyUp('a')}
                      onMouseLeave={() => handleKeyUp('a')}
                      onTouchStart={(e) => { e.preventDefault(); handleKeyDown('a'); }}
                      onTouchEnd={(e) => { e.preventDefault(); handleKeyUp('a'); }}
                      sx={{ 
                        width: 64, 
                        height: 64, 
                        bgcolor: activeKeys.has('a') ? 'primary.main' : 'rgba(255,255,255,0.1)',
                        '&:hover': { bgcolor: 'primary.dark' },
                        '&:disabled': { bgcolor: 'rgba(255,255,255,0.05)' },
                        transition: 'all 0.1s'
                      }}
                    >
                      <ArrowLeft sx={{ fontSize: 32 }} />
                    </IconButton>
                    
                    {/* Emergency Stop - bigger and more prominent */}
                    <IconButton 
                      size="large"
                      disabled={!localSlamActive || !isRobotOnline}
                      onClick={handleEmergencyStop}
                      sx={{ 
                        width: 64, 
                        height: 64, 
                        bgcolor: 'error.main',
                        '&:hover': { bgcolor: 'error.dark' },
                        '&:disabled': { bgcolor: 'rgba(255,255,255,0.05)' }
                      }}
                    >
                      <Stop sx={{ fontSize: 32 }} />
                    </IconButton>
                    
                    <IconButton 
                      size="large" 
                      disabled={!localSlamActive || !isRobotOnline}
                      onMouseDown={() => handleKeyDown('d')}
                      onMouseUp={() => handleKeyUp('d')}
                      onMouseLeave={() => handleKeyUp('d')}
                      onTouchStart={(e) => { e.preventDefault(); handleKeyDown('d'); }}
                      onTouchEnd={(e) => { e.preventDefault(); handleKeyUp('d'); }}
                      sx={{ 
                        width: 64, 
                        height: 64, 
                        bgcolor: activeKeys.has('d') ? 'primary.main' : 'rgba(255,255,255,0.1)',
                        '&:hover': { bgcolor: 'primary.dark' },
                        '&:disabled': { bgcolor: 'rgba(255,255,255,0.05)' },
                        transition: 'all 0.1s'
                      }}
                    >
                      <ArrowRight sx={{ fontSize: 32 }} />
                    </IconButton>
                  </Box>
                  
                  {/* Backward Button */}
                  <IconButton 
                    size="large" 
                    disabled={!localSlamActive || !isRobotOnline}
                    onMouseDown={() => handleKeyDown('s')}
                    onMouseUp={() => handleKeyUp('s')}
                    onMouseLeave={() => handleKeyUp('s')}
                    onTouchStart={(e) => { e.preventDefault(); handleKeyDown('s'); }}
                    onTouchEnd={(e) => { e.preventDefault(); handleKeyUp('s'); }}
                    sx={{ 
                      width: 64, 
                      height: 64, 
                      bgcolor: activeKeys.has('s') ? 'primary.main' : 'rgba(255,255,255,0.1)',
                      '&:hover': { bgcolor: 'primary.dark' },
                      '&:disabled': { bgcolor: 'rgba(255,255,255,0.05)' },
                      transition: 'all 0.1s'
                    }}
                  >
                    <ArrowDownward />
                  </IconButton>
                </Box>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Speed fontSize="small" />
                  <Typography variant="caption">Speed: {maxSpeed.toFixed(1)} m/s</Typography>
                </Box>
                <Slider 
                  size="small"
                  value={maxSpeed} 
                  onChange={(_, v) => setMaxSpeed(v as number)} 
                  min={0.1} 
                  max={0.5} 
                  step={0.05}
                  disabled={!localSlamActive}
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Tune fontSize="small" />
                  <Typography variant="caption">Rate: {commandRateHz} Hz</Typography>
                </Box>
                <Slider 
                  size="small"
                  value={commandRateHz} 
                  onChange={(_, v) => setCommandRateHz(v as number)} 
                  min={5} 
                  max={20} 
                  step={1}
                  marks={[{ value: 5, label: '5' }, { value: 10, label: '10' }, { value: 20, label: '20' }]}
                />
              </Box>

              {activeKeys.size > 0 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Sending at {commandRateHz} Hz
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Robot Position */}
          <Card>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MyLocation /> Robot Position
                </Box>
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)' }}>
                    <Typography variant="caption" color="text.secondary">X</Typography>
                    <Typography variant="body2">{robotPose.x.toFixed(2)} m</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)' }}>
                    <Typography variant="caption" color="text.secondary">Y</Typography>
                    <Typography variant="body2">{robotPose.y.toFixed(2)} m</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)' }}>
                    <Typography variant="caption" color="text.secondary">θ</Typography>
                    <Typography variant="body2">{(robotPose.theta * 180 / Math.PI).toFixed(0)}°</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Right side - Map visualization */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle2">
                  {slamMapData ? `Map: ${slamMapData.width}x${slamMapData.height} (${slamMapData.resolution.toFixed(3)} m/px)` : 'SLAM Map View'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton size="small" onClick={() => setZoom(Math.max(0.5, zoom - 0.25))}><ZoomOut /></IconButton>
                  <Typography variant="caption">{(zoom * 100).toFixed(0)}%</Typography>
                  <IconButton size="small" onClick={() => setZoom(Math.min(4, zoom + 0.25))}><ZoomIn /></IconButton>
                </Box>
              </Box>

              <Paper 
                sx={{ 
                  bgcolor: '#1a1a2e', 
                  borderRadius: 2, 
                  overflow: 'hidden',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: canvasHeight
                }}
              >
                {!selectedRobotId ? (
                  <Typography color="text.secondary">Select a robot to begin SLAM mapping</Typography>
                ) : !isRobotOnline ? (
                  <Typography color="text.secondary">Robot is offline</Typography>
                ) : !localSlamActive && !slamMapData ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <Explore sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography color="text.secondary">Click "Start SLAM" to begin mapping</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      The robot's launch file will be restarted with SLAM enabled
                    </Typography>
                  </Box>
                ) : (
                  <Stage width={canvasWidth} height={canvasHeight}>
                    <Layer>
                      {/* Map image */}
                      {mapImage && slamMapData && (
                        <KonvaImage
                          image={mapImage}
                          x={(canvasWidth - slamMapData.width * zoom) / 2}
                          y={(canvasHeight - slamMapData.height * zoom) / 2}
                          width={slamMapData.width * zoom}
                          height={slamMapData.height * zoom}
                        />
                      )}
                      
                      {/* Grid overlay when no map */}
                      {!mapImage && (
                        <>
                          {Array.from({ length: 21 }).map((_, i) => (
                            <Line
                              key={`h-${i}`}
                              points={[0, i * 25, canvasWidth, i * 25]}
                              stroke="rgba(255,255,255,0.1)"
                              strokeWidth={1}
                            />
                          ))}
                          {Array.from({ length: 29 }).map((_, i) => (
                            <Line
                              key={`v-${i}`}
                              points={[i * 25, 0, i * 25, canvasHeight]}
                              stroke="rgba(255,255,255,0.1)"
                              strokeWidth={1}
                            />
                          ))}
                        </>
                      )}

                      {/* Robot position indicator */}
                      <Circle
                        x={robotCanvasX}
                        y={robotCanvasY}
                        radius={12}
                        fill="#4CAF50"
                        stroke="#fff"
                        strokeWidth={2}
                      />
                      {/* Robot direction indicator */}
                      <Line
                        points={[
                          robotCanvasX,
                          robotCanvasY,
                          robotCanvasX + Math.cos(-robotPose.theta) * 20,
                          robotCanvasY + Math.sin(-robotPose.theta) * 20
                        ]}
                        stroke="#4CAF50"
                        strokeWidth={3}
                      />
                    </Layer>
                  </Stage>
                )}
              </Paper>

              {localSlamActive && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  SLAM is active. Drive the robot around to build the map. The map updates every few seconds.
                  {slamMapData && ` Updates: ${slamMapData.update_count}`}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Save Map Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save SLAM Map</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Save the current map before stopping SLAM. The map will be saved to the robot and uploaded to the cloud.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            After saving, click "Stop SLAM" to return to navigation mode.
          </Alert>
          <TextField
            autoFocus
            fullWidth
            label="Map Name"
            value={mapName}
            onChange={(e) => setMapName(e.target.value)}
            placeholder="e.g., office_floor1"
            helperText="Use lowercase letters, numbers, and underscores"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveMap} disabled={!mapName.trim()}>Save Map</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SlamViewer;
